import { consentManager } from "./consent";
import { HIGHLIGHT_CONFIG, BUILD_CONFIG } from "./config";

let highlightInitialized = false;
let highlightModule: typeof import("highlight.run") | null = null;

/**
 * Lazily load and initialize Highlight.io
 * Defers the ~80KB bundle until actually needed
 */
async function loadHighlight(): Promise<typeof import("highlight.run")["H"] | null> {
  if (!HIGHLIGHT_CONFIG.enabled) return null;
  
  try {
    if (!highlightModule) {
      highlightModule = await import("highlight.run");
    }
    
    if (!highlightInitialized) {
      const HIGHLIGHT_INGEST = HIGHLIGHT_CONFIG.useEU 
        ? 'https://pub.highlight.run'
        : 'https://pub.highlight.io';

      highlightModule.H.init(HIGHLIGHT_CONFIG.projectId, {
        environment: HIGHLIGHT_CONFIG.environment,
        version: BUILD_CONFIG.gitSha,
        backendUrl: HIGHLIGHT_INGEST,
        privacySetting: "strict",
        enableCanvasRecording: false,
        enablePerformanceRecording: false,
        tracingOrigins: [window.location.origin, "https://*.supabase.co"],
        networkRecording: {
          enabled: true,
          recordHeadersAndBody: false,
          urlBlocklist: ['auth/password', '/api/secret'],
        },
        consoleMethodsToRecord: import.meta.env.PROD ? ['error', 'warn'] : [],
        disableSessionRecording: true, // Start disabled, enable on consent
      });
      highlightInitialized = true;
    }
    
    return highlightModule.H;
  } catch (err) {
    console.warn('[ErrorTracking] Failed to load Highlight:', err);
    return null;
  }
}

/**
 * Initialize error tracking based on consent
 * Deferred — only loads the Highlight bundle when crash consent is granted
 */
export function initErrorTracking() {
  const consent = consentManager.getConsent();
  
  if (consent.crash && HIGHLIGHT_CONFIG.enabled) {
    // Use requestIdleCallback to avoid blocking main thread
    const init = () => {
      loadHighlight().then(H => {
        if (H) {
          H.start();
          console.log('[ErrorTracking] Highlight enabled (deferred, EU, errors-only)');
        }
      });
    };
    
    if ('requestIdleCallback' in window) {
      requestIdleCallback(init);
    } else {
      setTimeout(init, 1000);
    }
  } else {
    console.log('[ErrorTracking] Highlight disabled');
  }
}

/**
 * Update tracking when consent changes
 */
export function updateErrorTrackingConsent(crashConsent: boolean) {
  if (crashConsent) {
    loadHighlight().then(H => {
      if (H) {
        H.start();
        console.log('[ErrorTracking] Highlight enabled');
      }
    });
  } else {
    if (highlightModule) {
      highlightModule.H.stop();
      console.log('[ErrorTracking] Highlight disabled');
    }
  }
}

/**
 * Manually track an error (for custom error handling)
 */
export function trackError(error: Error, message?: string, metadata?: Record<string, any>) {
  const consent = consentManager.getConsent();
  if (!consent.crash) return;
  
  loadHighlight().then(H => {
    if (H) {
      H.consumeError(error, message || error.message, metadata);
    }
  });
}

/**
 * Consume error for error boundaries — fire-and-forget
 */
export function consumeError(error: Error, context: string, extra?: any) {
  if (highlightModule) {
    highlightModule.H.consumeError(error, context, extra);
  } else {
    // If highlight isn't loaded yet, try loading it
    loadHighlight().then(H => {
      if (H) H.consumeError(error, context, extra);
    });
  }
}
